import { useCallback, useEffect, useState } from "react";

import { isNavbarLoggedIn, navbarEmail, navbarInitials, rememberNavbarEmail } from "./auth";
import { subscribeToRouteChanges } from "./visibility";

export type NavbarAuthState = {
  loggedIn: boolean;
  email?: string;
  initials: string;
};

const AUTH_STORAGE_KEYS = new Set(["access_token", "refresh_token", "user_id", "user_name"]);

export const readNavbarAuth = (): NavbarAuthState => ({
  loggedIn: isNavbarLoggedIn(),
  email: navbarEmail(),
  initials: navbarInitials(),
});

const clearCachedProfileEmail = () => {
  try {
    sessionStorage.removeItem("navbar_profile_email");
  } catch {
    // Session storage can be unavailable in privacy-restricted embeds.
  }
};

export const useNavbarAuth = () => {
  const [auth, setAuth] = useState(() => {
    if (!isNavbarLoggedIn()) clearCachedProfileEmail();
    return readNavbarAuth();
  });
  const refresh = useCallback(() => {
    if (!isNavbarLoggedIn()) clearCachedProfileEmail();
    setAuth(readNavbarAuth());
  }, []);

  useEffect(() => {
    const handleAuth = (event: Event) => {
      rememberNavbarEmail((event as CustomEvent<{ email?: string }>).detail?.email);
      refresh();
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === null || AUTH_STORAGE_KEYS.has(event.key)) refresh();
    };

    window.addEventListener("AuthEvent", handleAuth);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    // Logout clears storage in this tab and then navigates in-app, which fires none of the events above.
    const unsubscribeRoute = subscribeToRouteChanges(refresh);

    return () => {
      window.removeEventListener("AuthEvent", handleAuth);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
      unsubscribeRoute();
    };
  }, [refresh]);

  return auth;
};
